import React, { useState, useRef, useCallback } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X, Upload, File, AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';
import { useAuth } from '../../lib/auth/AuthProvider';
import { useStore, Collection, Folder, CollectionRequest, HttpRequest, KeyValue, CollectionAuth } from '../../stores';
import { RequestBody, RequestAuth } from '../../db/schema';
import { generateId } from '../../lib/utils';

interface ImportCollectionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface ImportResult {
  success: boolean;
  message: string;
  collectionName?: string;
  requestCount?: number;
  folderCount?: number;
}

// Postman collection types (simplified for parsing)
interface PostmanAuth {
  type: string;
  bearer?: Array<{ key: string; value: string }>;
  basic?: Array<{ key: string; value: string }>;
  apikey?: Array<{ key: string; value: string; in?: string }>;
  hawk?: Array<{ key: string; value: string }>;
}

interface PostmanEvent {
  listen: 'prerequest' | 'test';
  script?: {
    exec?: string | string[];
    type?: string;
  };
}

interface PostmanCollection {
  info: {
    name: string;
    description?: string;
    schema: string;
  };
  item: PostmanItem[];
  variable?: PostmanVariable[];
  auth?: PostmanAuth;
  event?: PostmanEvent[];
}

interface PostmanItem {
  name: string;
  request?: PostmanRequest;
  item?: PostmanItem[]; // For folders
  auth?: PostmanAuth;
  event?: PostmanEvent[];
}

interface PostmanRequest {
  method: string;
  header?: Array<{ key: string; value: string; disabled?: boolean; description?: string }>;
  url: string | { raw: string; query?: Array<{ key: string; value: string; disabled?: boolean }> };
  body?: {
    mode: string;
    raw?: string;
    urlencoded?: Array<{ key: string; value: string; disabled?: boolean }>;
    formdata?: Array<{ key: string; value: string; disabled?: boolean }>;
    options?: { raw?: { language?: string } };
  };
  auth?: PostmanAuth;
}

interface PostmanVariable {
  key: string;
  value: string;
}

// Helper functions to extract request data from Postman format
function extractUrl(url: string | { raw: string; query?: Array<{ key: string; value: string; disabled?: boolean }> } | undefined): string {
  if (!url) return '';
  if (typeof url === 'string') return url;
  return url.raw || '';
}

function extractParams(url: string | { raw: string; query?: Array<{ key: string; value: string; disabled?: boolean }> } | undefined): KeyValue[] {
  if (!url || typeof url === 'string') return [];
  if (!url.query) return [];
  return url.query.map(q => ({
    key: q.key || '',
    value: q.value || '',
    enabled: !q.disabled,
  }));
}

function extractHeaders(headers: Array<{ key: string; value: string; disabled?: boolean; description?: string }> | undefined): KeyValue[] {
  if (!headers) return [];
  return headers.map(h => ({
    key: h.key || '',
    value: h.value || '',
    description: h.description,
    enabled: !h.disabled,
  }));
}

function extractBody(body: { mode: string; raw?: string; urlencoded?: Array<{ key: string; value: string; disabled?: boolean }>; formdata?: Array<{ key: string; value: string; disabled?: boolean }>; options?: { raw?: { language?: string } } } | undefined): RequestBody | undefined {
  if (!body) return undefined;

  switch (body.mode) {
    case 'raw':
      const contentType = body.options?.raw?.language === 'json' ? 'json'
        : body.options?.raw?.language === 'xml' ? 'xml'
        : body.options?.raw?.language === 'html' ? 'html'
        : body.options?.raw?.language === 'javascript' ? 'javascript'
        : 'text';
      return {
        type: 'raw',
        raw: {
          content: body.raw || '',
          contentType,
        },
      };
    case 'urlencoded':
      return {
        type: 'x-www-form-urlencoded',
        urlencoded: (body.urlencoded || []).map(p => ({
          key: p.key || '',
          value: p.value || '',
          enabled: !p.disabled,
        })) as any,
      };
    case 'formdata':
      return {
        type: 'form-data',
        formData: (body.formdata || []).map(p => ({
          key: p.key || '',
          value: p.value || '',
          enabled: !p.disabled,
        })) as any,
      };
    default:
      return { type: 'none' };
  }
}

function extractAuth(auth: PostmanAuth | undefined): RequestAuth | undefined {
  if (!auth || auth.type === 'noauth') return { type: 'none' };

  const findValue = (arr: Array<{ key: string; value: string }> | undefined, key: string): string => {
    return arr?.find(item => item.key === key)?.value || '';
  };

  switch (auth.type) {
    case 'bearer':
      return {
        type: 'bearer',
        bearer: { token: findValue(auth.bearer, 'token') },
      };
    case 'basic':
      return {
        type: 'basic',
        basic: {
          username: findValue(auth.basic, 'username'),
          password: findValue(auth.basic, 'password'),
        },
      };
    case 'apikey':
      const addTo = auth.apikey?.find(a => a.key === 'in')?.value;
      return {
        type: 'api-key',
        apiKey: {
          key: findValue(auth.apikey, 'key'),
          value: findValue(auth.apikey, 'value'),
          addTo: addTo === 'query' ? 'query' : 'header',
        },
      };
    case 'hawk':
      return {
        type: 'hawk',
        hawk: {
          authId: findValue(auth.hawk, 'authId'),
          authKey: findValue(auth.hawk, 'authKey'),
          algorithm: (findValue(auth.hawk, 'algorithm') as 'sha256' | 'sha1') || 'sha256',
          includePayloadHash: false,
        },
      };
    default:
      return { type: 'none' };
  }
}

function extractScripts(events: PostmanEvent[] | undefined): { preRequest?: string; postRequest?: string } | undefined {
  if (!events || events.length === 0) return undefined;

  const scripts: { preRequest?: string; postRequest?: string } = {};

  for (const event of events) {
    const scriptContent = Array.isArray(event.script?.exec)
      ? event.script.exec.join('\n')
      : event.script?.exec || '';

    if (event.listen === 'prerequest' && scriptContent.trim()) {
      scripts.preRequest = scriptContent;
    } else if (event.listen === 'test' && scriptContent.trim()) {
      scripts.postRequest = scriptContent;
    }
  }

  return (scripts.preRequest || scripts.postRequest) ? scripts : undefined;
}

function extractCollectionAuth(auth: PostmanAuth | undefined): CollectionAuth | undefined {
  if (!auth || auth.type === 'noauth') return { type: 'none' };

  const findValue = (arr: Array<{ key: string; value: string }> | undefined, key: string): string => {
    return arr?.find(item => item.key === key)?.value || '';
  };

  switch (auth.type) {
    case 'bearer':
      return {
        type: 'bearer',
        bearer: { token: findValue(auth.bearer, 'token') },
      };
    case 'basic':
      return {
        type: 'basic',
        basic: {
          username: findValue(auth.basic, 'username'),
          password: findValue(auth.basic, 'password'),
        },
      };
    case 'apikey':
      const addTo = auth.apikey?.find(a => a.key === 'in')?.value;
      return {
        type: 'api-key',
        apiKey: {
          key: findValue(auth.apikey, 'key'),
          value: findValue(auth.apikey, 'value'),
          addTo: addTo === 'query' ? 'query' : 'header',
        },
      };
    case 'hawk':
      return {
        type: 'hawk',
        hawk: {
          authId: findValue(auth.hawk, 'authId'),
          authKey: findValue(auth.hawk, 'authKey'),
          algorithm: (findValue(auth.hawk, 'algorithm') as 'sha256' | 'sha1') || 'sha256',
          includePayloadHash: false,
        },
      };
    default:
      return { type: 'none' };
  }
}

export function ImportCollectionDialog({ open, onOpenChange }: ImportCollectionDialogProps) {
  const { getAccessToken, config } = useAuth();
  const { activeWorkspaceId, addCollection, setLocalRequestDataBatch } = useStore();
  const [isDragging, setIsDragging] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isImporting, setIsImporting] = useState(false);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const resetState = () => {
    setSelectedFile(null);
    setIsImporting(false);
    setImportResult(null);
    setError(null);
    setIsDragging(false);
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      resetState();
    }
    onOpenChange(newOpen);
  };

  const validateFile = (file: File): boolean => {
    // Check file type
    if (!file.name.endsWith('.json')) {
      setError('Please select a JSON file');
      return false;
    }

    // Check file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setError('File size must be less than 10MB');
      return false;
    }

    return true;
  };

  const handleFileSelect = (file: File) => {
    setError(null);
    setImportResult(null);

    if (validateFile(file)) {
      setSelectedFile(file);
    }
  };

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  // Convert Postman collection to our format (for local-only import)
  const convertPostmanToLocal = (postmanCollection: PostmanCollection): {
    collection: Collection;
    requestCount: number;
    folderCount: number;
    requestDataMap: Record<string, HttpRequest>;
  } => {
    const folders: Folder[] = [];
    const requests: CollectionRequest[] = [];
    const requestDataMap: Record<string, HttpRequest> = {};
    let requestCount = 0;
    let folderCount = 0;

    const processItem = (item: PostmanItem, parentFolderId?: string, inheritedAuth?: PostmanAuth): void => {
      if (item.request) {
        // It's a request - generate ID and extract full data
        const requestId = generateId();
        const method = (item.request.method || 'GET').toUpperCase();

        // Add to collection structure (minimal data)
        const request: CollectionRequest = {
          id: requestId,
          name: item.name,
          method,
          folderId: parentFolderId,
        };
        requests.push(request);
        requestCount++;

        // Use request auth, then item auth, then inherited (folder/collection) auth
        const effectiveAuth = item.request.auth || item.auth || inheritedAuth;
        const scripts = extractScripts(item.event);

        // Store full request data
        const fullRequest: HttpRequest = {
          id: requestId,
          name: item.name,
          method: method as HttpRequest['method'],
          url: extractUrl(item.request.url),
          headers: extractHeaders(item.request.header),
          params: extractParams(item.request.url),
          body: extractBody(item.request.body),
          auth: extractAuth(effectiveAuth),
          scripts,
        };
        requestDataMap[requestId] = fullRequest;
      } else if (item.item) {
        // It's a folder
        const folder: Folder = {
          id: generateId(),
          name: item.name,
          parentId: parentFolderId,
          requests: [],
        };
        folders.push(folder);
        folderCount++;

        // Use folder's auth or inherit from parent
        const folderAuth = item.auth || inheritedAuth;

        // Process nested items with inherited auth
        for (const nestedItem of item.item) {
          processItem(nestedItem, folder.id, folderAuth);
        }
      }
    };

    // Process all top-level items with collection-level auth
    const collectionAuth = postmanCollection.auth;
    for (const item of postmanCollection.item) {
      processItem(item, undefined, collectionAuth);
    }

    // Assign requests to folders
    for (const folder of folders) {
      folder.requests = requests
        .filter(r => r.folderId === folder.id)
        .map(r => ({ id: r.id, name: r.name, method: r.method }));
    }

    // Convert collection-level auth to CollectionAuth format
    const collectionAuthConfig = collectionAuth ? extractCollectionAuth(collectionAuth) : undefined;

    const collection: Collection = {
      id: generateId(),
      name: postmanCollection.info.name,
      description: postmanCollection.info.description,
      workspaceId: activeWorkspaceId || 'local-workspace',
      folders,
      requests: requests
        .filter(r => !r.folderId)
        .map(r => ({ id: r.id, name: r.name, method: r.method })),
      auth: collectionAuthConfig,
    };

    return { collection, requestCount, folderCount, requestDataMap };
  };

  const handleImport = async () => {
    if (!selectedFile) return;

    setIsImporting(true);
    setError(null);
    setImportResult(null);

    try {
      // Read and parse the file
      const fileContent = await selectedFile.text();
      let postmanCollection: PostmanCollection;

      try {
        postmanCollection = JSON.parse(fileContent);
      } catch {
        throw new Error('Invalid JSON format');
      }

      // Validate it's a Postman collection
      if (!postmanCollection.info || !postmanCollection.item) {
        throw new Error('Invalid Postman collection format. Missing "info" or "item" fields.');
      }

      // Check if it's a v2.1 collection
      const schema = postmanCollection.info.schema || '';
      if (!schema.includes('v2.1') && !schema.includes('v2.0')) {
        console.warn('Collection schema might not be v2.x, attempting to import anyway');
      }

      // Try to import via API if we have a workspace and auth
      const token = await getAccessToken();
      const workspaceId = activeWorkspaceId;

      if (token && workspaceId && workspaceId !== 'local-workspace' && config?.api?.endpoint) {
        // Import via backend API
        const response = await fetch(
          `${config.api.endpoint}/workspaces/${workspaceId}/import/postman`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ collection: postmanCollection }),
          }
        );

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || `Import failed: ${response.status}`);
        }

        const result = await response.json();

        // Update local state with the imported collection
        if (result.data?.collection) {
          addCollection(result.data.collection);
        }

        setImportResult({
          success: true,
          message: 'Collection imported successfully!',
          collectionName: postmanCollection.info.name,
          requestCount: result.data?.requestCount,
          folderCount: result.data?.folderCount,
        });
      } else {
        // Import locally (no backend sync)
        const { collection, requestCount, folderCount, requestDataMap } = convertPostmanToLocal(postmanCollection);
        addCollection(collection);

        // Store full request data in local store for later retrieval
        if (Object.keys(requestDataMap).length > 0) {
          setLocalRequestDataBatch(requestDataMap);
          console.log('[Import] Stored local request data for', Object.keys(requestDataMap).length, 'requests');
        }

        setImportResult({
          success: true,
          message: 'Collection imported locally!',
          collectionName: collection.name,
          requestCount,
          folderCount,
        });
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to import collection';
      setError(message);
    } finally {
      setIsImporting(false);
    }
  };

  const handleClose = () => {
    handleOpenChange(false);
  };

  return (
    <Dialog.Root open={open} onOpenChange={handleOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50 z-50" />
        <Dialog.Content className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-card border border-border rounded-lg shadow-xl z-50 w-[500px] max-h-[85vh] overflow-hidden">
          <div className="flex items-center justify-between px-6 py-4 border-b border-border">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500/10 rounded-lg">
                <Upload className="w-5 h-5 text-blue-500" />
              </div>
              <Dialog.Title className="text-lg font-semibold">
                Import Collection
              </Dialog.Title>
            </div>
            <Dialog.Close asChild>
              <button className="p-1 rounded hover:bg-accent transition-colors">
                <X className="w-5 h-5 text-muted-foreground" />
              </button>
            </Dialog.Close>
          </div>

          <div className="p-6 space-y-4">
            {/* Drop zone */}
            <div
              className={`
                border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer
                ${isDragging
                  ? 'border-primary bg-primary/5'
                  : selectedFile
                    ? 'border-green-500 bg-green-500/5'
                    : 'border-border hover:border-muted-foreground'
                }
              `}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={handleBrowseClick}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".json"
                className="hidden"
                onChange={handleInputChange}
              />

              {selectedFile ? (
                <div className="flex flex-col items-center gap-2">
                  <File className="w-10 h-10 text-green-500" />
                  <p className="text-sm font-medium">{selectedFile.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {(selectedFile.size / 1024).toFixed(1)} KB
                  </p>
                  <button
                    type="button"
                    className="text-xs text-primary hover:underline mt-2"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedFile(null);
                      setError(null);
                      setImportResult(null);
                    }}
                  >
                    Choose a different file
                  </button>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2">
                  <Upload className={`w-10 h-10 ${isDragging ? 'text-primary' : 'text-muted-foreground'}`} />
                  <p className="text-sm font-medium">
                    {isDragging ? 'Drop your file here' : 'Drag & drop your collection file'}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    or click to browse
                  </p>
                </div>
              )}
            </div>

            {/* Supported formats */}
            <p className="text-xs text-muted-foreground">
              Supported formats: Postman Collection v2.1 (.json)
            </p>

            {/* Error message */}
            {error && (
              <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-500">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <p className="text-sm">{error}</p>
              </div>
            )}

            {/* Success message */}
            {importResult?.success && (
              <div className="flex items-start gap-2 p-3 bg-green-500/10 border border-green-500/20 rounded-lg text-green-500">
                <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium">{importResult.message}</p>
                  {importResult.collectionName && (
                    <p className="text-green-400 mt-1">
                      Collection: {importResult.collectionName}
                    </p>
                  )}
                  {(importResult.requestCount !== undefined || importResult.folderCount !== undefined) && (
                    <p className="text-green-400 text-xs mt-1">
                      {importResult.folderCount ? `${importResult.folderCount} folder(s), ` : ''}
                      {importResult.requestCount} request(s) imported
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-2 px-6 py-4 border-t border-border bg-muted/30">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-accent rounded-md transition-colors"
            >
              {importResult?.success ? 'Close' : 'Cancel'}
            </button>
            {!importResult?.success && (
              <button
                type="button"
                onClick={handleImport}
                disabled={!selectedFile || isImporting}
                className="px-4 py-2 text-sm font-medium bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
              >
                {isImporting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Importing...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4" />
                    Import
                  </>
                )}
              </button>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
